import { lazy, Suspense, useEffect, useState } from "react";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { Navigate, useLocation } from "react-router-dom";

import { config } from "../config/env.config";
import { apiService } from "../services";
import {
  BENCHMARK_AUTH_READY,
  getTrustedBenchmarkToken,
  getTrustedTalentHubOrigin,
} from "./model-benchmark-auth";

const ModelBenchmark = lazy(async () => {
  const module = await import("../components/ModelBenchmark");

  return { default: module.ModelBenchmark };
});

const BenchmarkResults = lazy(async () => {
  const module = await import("../components/ModelBenchmark");

  return { default: module.BenchmarkResults };
});

export function LocalModelBenchmarkRoute() {
  const location = useLocation();
  const requiresTalentHubAuth = config.appEnv !== "development";
  const [isAuthorized, setIsAuthorized] = useState(!requiresTalentHubAuth);
  const talentHubOrigin = requiresTalentHubAuth
    ? getTrustedTalentHubOrigin(config.talentHubOrigin)
    : null;
  const authenticationError = !requiresTalentHubAuth
    ? null
    : !talentHubOrigin
      ? "Model benchmark authentication is not configured."
      : window.parent === window
        ? "Open the model benchmark from TalentHub."
        : null;

  useEffect(() => {
    if (!requiresTalentHubAuth || !talentHubOrigin || authenticationError) {
      return;
    }

    const receiveToken = (event: MessageEvent) => {
      const token = getTrustedBenchmarkToken(
        event,
        window.parent,
        talentHubOrigin,
      );
      if (!token) return;

      apiService.setBenchmarkAccessToken(token);
      setIsAuthorized(true);
    };

    window.addEventListener("message", receiveToken);
    window.parent.postMessage({ type: BENCHMARK_AUTH_READY }, talentHubOrigin);

    return () => {
      window.removeEventListener("message", receiveToken);
      apiService.setBenchmarkAccessToken(null);
    };
  }, [authenticationError, requiresTalentHubAuth, talentHubOrigin]);

  if (!config.modelBenchmarkEnabled) {
    return <Navigate replace to="/" />;
  }

  if (authenticationError) {
    return (
      <Center mih="100vh">
        <Stack align="center" gap="xs">
          <Text fw={600}>Model benchmark unavailable</Text>
          <Text c="dimmed" size="sm">
            {authenticationError}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthorized) {
    return (
      <Center mih="100vh">
        <Stack align="center" gap="xs">
          <Loader aria-label="Authenticating model benchmark" />
          <Text c="dimmed" size="sm">
            Authenticating with TalentHub
          </Text>
        </Stack>
      </Center>
    );
  }

  const RouteComponent = location.pathname.endsWith("/results")
    ? BenchmarkResults
    : ModelBenchmark;

  return (
    <Suspense
      fallback={
        <Center mih="100vh">
          <Loader aria-label="Loading model benchmark" />
        </Center>
      }
    >
      <RouteComponent />
    </Suspense>
  );
}
